import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, AlertCircle, LogIn, Heart, Clock, Users, Shield, Phone, ArrowRight, Zap } from 'lucide-react';
import SEOHead from '../SEO/SEOHead';
import EmergencyForm from './EmergencyForm';
import EmergencyProcess from './EmergencyProcess';
import { useAuth } from '../../context/AuthContext';

export default function EmergencyBlood() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Structured data for emergency blood service
  const emergencyStructuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Emergency Blood Request Service",
    "description": "24/7 emergency blood request service for critical situations. Quick donor matching and immediate response.",
    "provider": {
      "@type": "Organization",
      "name": "BloodHero",
      "url": "https://www.thebloodhero.com"
    },
    "serviceType": "Emergency Blood Donation",
    "areaServed": {
      "@type": "Country",
      "name": "India"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": "https://www.thebloodhero.com/emergency-blood",
      "servicePhone": "+91-9992396623"
    },
    "hoursAvailable": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday", "Tuesday", "Wednesday", "Thursday", 
        "Friday", "Saturday", "Sunday"
      ],
      "opens": "00:00",
      "closes": "23:59"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Emergency Blood Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Immediate Blood Donor Matching"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "24/7 Emergency Response"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Critical Blood Type Identification"
          }
        }
      ]
    }
  };

  return (
    <>
      <SEOHead 
        title="Emergency Blood Request India | 24/7 Blood Donation Service | BloodHero"
        description="Emergency blood request India - 24/7 blood donation service for critical situations. Quick blood donor matching, emergency blood bank coordination. Call +91-9992396623 for immediate blood donation assistance."
        keywords="emergency blood request India, emergency blood donation, 24/7 blood donation service, blood donor matching, emergency blood bank, critical blood need, urgent blood donation, blood donation emergency, immediate blood donor, blood donation assistance"
        canonicalUrl="/emergency-blood"
        ogImage="/images/emergency-blood-og.jpg"
        ogType="service"
        structuredData={emergencyStructuredData}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50">
        {/* Hero Section with H1 */}
        <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 bg-gradient-to-br from-red-600 via-red-700 to-red-800"></div>
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
          
          {/* Floating Elements */}
          <motion.div
            animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 left-10 w-16 h-16 bg-red-400/20 rounded-full blur-xl"
          />
          <motion.div
            animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-20 right-10 w-20 h-20 bg-red-300/20 rounded-full blur-xl"
          />

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto text-center">
              {/* Emergency Badge */}
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 backdrop-blur-sm border border-red-400/30 rounded-full text-red-100 mb-6"
              >
                <Zap className="w-4 h-4" />
                <span className="text-sm font-semibold">24/7 Emergency Service</span>
              </motion.div>

              {/* Main H1 Heading for SEO */}
              <motion.h1 
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7 }}
                className="text-5xl md:text-7xl font-bold mb-6 text-white leading-tight"
              >
                Emergency
                <span className="block text-red-200">Blood Request</span>
              </motion.h1>
              
              <motion.p 
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="text-xl md:text-2xl text-red-100 mb-8 max-w-3xl mx-auto leading-relaxed"
              >
                Quick assistance for critical situations. Connect with donors instantly and save lives when every second counts.
              </motion.p>

              {/* Stats */}
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-4xl mx-auto"
              >
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="flex items-center justify-center w-12 h-12 bg-red-500/30 rounded-xl mb-4 mx-auto">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">24/7</h3>
                  <p className="text-red-100">Available Round the Clock</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="flex items-center justify-center w-12 h-12 bg-red-500/30 rounded-xl mb-4 mx-auto">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">1000+</h3>
                  <p className="text-red-100">Active Donors</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="flex items-center justify-center w-12 h-12 bg-red-500/30 rounded-xl mb-4 mx-auto">
                    <Heart className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">500+</h3>
                  <p className="text-red-100">Lives Saved</p>
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/search-requests')}
                  className="flex items-center gap-2 px-8 py-4 bg-white text-red-600 font-bold rounded-2xl shadow-2xl hover:bg-red-50 transition-all duration-300"
                >
                  <Search className="w-5 h-5" />
                  Search Blood Requests
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/register-donor')}
                  className="flex items-center gap-2 px-8 py-4 bg-red-500 text-white font-bold rounded-2xl shadow-2xl hover:bg-red-600 transition-all duration-300"
                >
                  <Heart className="w-5 h-5" />
                  Become a Donor
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Emergency Form Section with H2 */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Need Blood Urgently?
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Fill out the emergency blood request form below. Our team will immediately start searching for compatible donors in your area.
                </p>
              </div>
              <EmergencyForm />
            </div>
          </div>
        </section>

        {/* Emergency Process Section with H2 */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  How Emergency Blood Requests Work
                </h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  Our streamlined process ensures quick response times for critical blood needs
                </p>
              </div>
              <EmergencyProcess />
            </div>
          </div>
        </section>

        {/* Contact Information Section with H2 */}
        <section className="py-16 bg-red-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Need Immediate Assistance?
              </h2>
              <p className="text-xl mb-8 text-red-100">
                Call our emergency helpline for immediate support
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <div className="flex items-center gap-3">
                  <Phone className="w-6 h-6" />
                  <span className="text-2xl font-bold">+91-9992396623</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-6 h-6" />
                  <span className="text-xl">24/7 Available</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}